import { computed, ref } from 'vue'

import { useConfigStore } from '@toni/rendering/src/stores/config'
import { usePageStore } from '@toni/rendering/src/stores/page'

import { createImgSrc } from '../components/bauer-image/image-utils'

import type { MetaData } from './meta-data-schema'
import type { PageMeta } from './page-meta-schema'

export function generateMetas (consentConfig: any, metaData: MetaData, pageMeta: PageMeta) {
  const configStore = useConfigStore()
  const rsConfig = ref(configStore.rsConfig)

  const brand = usePageStore().brandFromStore as string
  const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
  const htmlHeadLinks = rsConfig.value.htmlHeadLinks || []
  const htmlHeadMetas = rsConfig.value.htmlHeadMetas || []
  const canonicalAbsoluteUrl = metaData?.canonical

  const setHtmlHeadScriptSources = []

  const publicationName = rsConfig.value.actualBrandName
  const metaTitle = computed(() => {
    return metaData.metaTitle ? metaData.metaTitle + ' | ' + publicationName : publicationName
  })

  if (metaData?.structuredData) {
    metaData.structuredData.forEach((elem) => {
      const structuredData = {
        innerHTML: JSON.stringify(elem),
        'data-vmid': 'structured-data',
        type: 'application/ld+json'
      }
      setHtmlHeadScriptSources.push(structuredData)
    })
  }

  // *********** IMPORTANT *********** These scripts must be the first scripts to load and in this order!!!!
  // Sourcepoint needs to be loaded for additional consent (embeds) and to open the consent manager on the datenschutz page
  // even if consent is not really required with PUR abo. For these cases we just hide the popup with the `hidePrivacyManager` setting below
  // consentConfig is from sourcepointStore and imported by component that uses this composable
  // Only needed to display the data protection manager manually

  setHtmlHeadScriptSources.unshift({
    'data-vmid': 'open-privacy-manager',
    type: 'text/javascript',
    async: false,
    innerHTML: `window.openPrivacyManager = function() {
            window._sp_.gdpr.loadPrivacyManagerModal(${consentConfig.privacyManagerId})
          }`
  })
  setHtmlHeadScriptSources.unshift({
    'data-vmid': 'wrapper-without-detection',
    type: 'text/javascript',
    async: true,
    src: consentConfig.wrapperWithoutDetection
  })

  /*
   * isSPA value and gdpr.targetingParams.acps value in window._sp_ object are needed for ContentPass integration
   * read more about ContentPass in the docs https://docs.contentpass.net/docs/onboarding/cmp/sourcepoint#4-sourcepoint-contentpass-integration
   *
   */

  setHtmlHeadScriptSources.unshift({
    'data-vmid': 'sp-config',
    type: 'text/javascript',
    async: false,
    innerHTML: `
      window._sp_queue = []
      window._sp_ = {
        config: {
          accountId: ${consentConfig.accountId},
          propertyId: ${consentConfig.propertyId},
          baseEndpoint: '${consentConfig.privacyBaseEndpoint}',
          isSPA: true,
          gdpr: {
            targetingParams: {
              acps: 'false'
            }
          },
          events: {
            onMessageReady: function() {
              // console.log('[event] onMessageReady', arguments);
            },
            onMessageReceiveData: function() {
              // console.log('[event] onMessageReceiveData', arguments);
            },
            onSPPMObjectReady: function() {
              // console.log('[event] onSPPMObjectReady', arguments);
            },
            onSPReady: function() {
              // console.log('[event] onSPReady', arguments);
            },
            onError: function() {
              console.error('[event] onError', arguments);
            },
            onMessageChoiceSelect: function() {
              // console.log('[event] onMessageChoiceSelect', arguments);
            },
            onConsentReady: function(consentUUID, euconsent) {
              // console.log('[event] onConsentReady', arguments);
            },
            onPrivacyManagerAction: function() {
              // console.log('[event] onPrivacyManagerAction', arguments);
            },
            onPMCancel: function() {
              // console.log('[event] onPMCancel', arguments);
            }
          }
        }
      }
    `
  })
  setHtmlHeadScriptSources.unshift({
    'data-vmid': 'gdpr-stub',
    type: 'text/javascript',
    async: false,
    src: '/assets/js/gdpr-stub_toni.js'
  })

  const links = [
    {
      rel: 'canonical',
      href: canonicalAbsoluteUrl
    }
  ]

  links.push(...htmlHeadLinks)

  const robotsContentMaxImagePreview = pageMeta?.pageType === 'article' ? ',max-image-preview:large' : ''

  return {
    htmlAttrs: { lang: 'de' },
    title: metaTitle,
    meta: [
      ...(metaData?.metaDescription
        ? [{
            name: 'description',
            'data-vmid': 'description',
            content: metaData.metaDescription
          }]
        : []),
      ...(metaData?.ogTitle
        ? [{
            name: 'og:title',
            'data-vmid': 'og-title',
            content: metaData.ogTitle
          }]
        : []),
      ...(metaData?.ogDescription
        ? [{
            name: 'og:description',
            'data-vmid': 'og-description',
            content: metaData.ogDescription
          }]
        : []),
      ...(metaData?.ogImage
        ? [{
            name: 'og:image',
            'data-vmid': 'og-image',
            content: createImgSrc({
              image: metaData.ogImage,
              brand,
              imageServiceUrl,
              setName: 'ogImage'
            }, 1600)?.href ?? ''
          }]
        : []),
      {
        name: 'robots',
        'data-vmid': 'robots',
        content: metaData?.noIndex
          ? `noindex${robotsContentMaxImagePreview}${metaData.noIndexFollow ? ',follow' : ''}`
          : `index,follow${robotsContentMaxImagePreview}`
      },
      ...htmlHeadMetas
    ],
    link: links,
    script: setHtmlHeadScriptSources
  }
}
